import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { SEOHeadTags, useSEO } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articleContents } from '@/mocks/articleData';
import { articles } from '@/mocks/videoData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articleContents[slug || ''];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-6">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-article-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const seo = useSEO({
    title: `${article.title} - 国产视频在线观看`,
    description: article.excerpt,
    keywords: `${article.title},国产视频,影视资讯,${article.category}`,
    canonicalUrl: `https://guochan.video/article/${article.slug}`,
    ogType: 'article',
    ogImage: article.image,
    articlePublishedTime: article.date,
    articleModifiedTime: article.date,
  });

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '国产视频在线观看',
    },
  };

  return (
    <>
      <SEOHeadTags data={seo} />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article className="pt-24 pb-6">
            <div className="max-w-[800px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '影视资讯', href: '/' },
                  { label: article.title },
                ]}
              />

              <div className="mt-6">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.readCount} 阅读
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-3 pb-6 border-b border-background-200/70">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary-600">{article.author[0]}</span>
                  </div>
                  <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
                </div>
              </div>

              <div className="mt-8">
                <div className="w-full aspect-[16/9] md:aspect-[2.5/1] overflow-hidden rounded-xl bg-background-200 mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                  prose-strong:text-foreground-900 prose-strong:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-14 md:py-20">
              <div className="max-w-[800px] mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="space-y-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                    >
                      <div className="w-full sm:w-[160px] flex-shrink-0 aspect-[16/10] sm:aspect-[4/3] overflow-hidden rounded-lg bg-background-200">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {rel.title}
                        </h3>
                        <p className="text-sm text-foreground-500 mt-1.5 line-clamp-2">{rel.excerpt}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                          <span>{rel.date}</span>
                          <span>{rel.readCount} 阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}